import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import {
  transferTypes,
  useSimTransferStateInfo,
} from "@/hooks/useSimTransferStateInfo";
import { useStartAndFetchEsimChange } from "@/hooks/useStartAndFetchEsimChange";
import { useStartESimChange, useStartESimReset } from "@/services/api";
import { ESimInfo } from "@/services/model";
import { esimTransferTypeAtom } from "@/utils/atoms";
import {
  checkIfDeviceCanReloadEsim,
  checkIfDeviceCanTransferEsim,
  isEsimchangeInProgress,
  isEsimresetInProgress,
} from "@/utils/deviceUtils";

import { DetailsSection } from "./DetailsEsimDisplay";
import { EsimDisabledActionsTooltip } from "./EsimDisabledActionsTooltip/EsimDisabledActionsTooltip";
import { EsimTransferFlow } from "./EsimTransferFlow";
import { SimDetailActionRow } from "./SimDetailActionRow";

export const SimDetailActions = ({
  esimInfo,
  simId,
  activeSection,
  setActiveSection,
  isBlocked,
}: {
  esimInfo: ESimInfo[];
  simId: string;
  activeSection: DetailsSection | null;
  setActiveSection: (section: DetailsSection | null) => void;
  isBlocked: boolean;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });
  const { t: t2 } = useTranslation();
  const canReloadEsim = checkIfDeviceCanReloadEsim(esimInfo);
  const canChangeEsim = checkIfDeviceCanTransferEsim(esimInfo);

  const esimReloadDisabledReasons =
    esimInfo?.[0]?.esimResetNotAllowedReason || [];
  const esimTransferDisabledReasons =
    esimInfo?.[0]?.esimTransferNotAllowedReason || [];

  const { data: simDetail, refetch: refetchSimDetail } = useDisplayedSim();
  const setEsimTransferType = useSetAtom(esimTransferTypeAtom);

  const { startChange: startReload, changeInProgress: reloadInProgress } =
    useStartAndFetchEsimChange(
      refetchSimDetail,
      isEsimresetInProgress,
      simId ? Number(simId) : undefined,
      useStartESimReset,
      transferTypes.RELOAD,
    );
  const startedReload = useSimTransferStateInfo(simId, transferTypes.RELOAD);

  const { startChange: startTransfer, changeInProgress: transferInProgress } =
    useStartAndFetchEsimChange(
      refetchSimDetail,
      isEsimchangeInProgress,
      simId ? Number(simId) : undefined,
      useStartESimChange,
      transferTypes.TRANSFER,
    );
  const startedTransfer = useSimTransferStateInfo(
    simId,
    transferTypes.TRANSFER,
  );

  // A reset/transfer started elsewhere (or before a page reload) still shows
  // up as in-progress from the API - reopen the matching inline flow so the
  // user lands on its waiting/ready state instead of the closed row list.
  useEffect(() => {
    if (activeSection !== null) {
      return;
    }

    if (reloadInProgress) {
      setActiveSection("reloadEsim");
    } else if (transferInProgress) {
      setActiveSection("transferEsim");
    }
  }, [activeSection, reloadInProgress, transferInProgress, setActiveSection]);

  // esimTransferTypeAtom only ever gets set (see useStartAndFetchEsimChange's
  // onSuccess) - closing the "done" screen is the one moment we know the
  // user is finished with it, so this is where it has to be cleared. Left
  // set, `startedReload`/`startedTransfer` would stay true for the rest of
  // the session and reopening the row would jump straight back to "done"
  // instead of the confirm step, with no way to start a second change.
  const closeSection = () => {
    setActiveSection(null);

    const numericSimId = parseInt(simId);
    if (!isNaN(numericSimId)) {
      setEsimTransferType(({ [numericSimId]: _removed, ...rest }) => rest);
    }
  };

  if (activeSection === "reloadEsim") {
    return (
      <EsimTransferFlow
        dataTestid="reload-esim"
        cardTitle={t("reloadEsim.screens.confirm.title")}
        confirmDescription={
          <Trans t={t} i18nKey="reloadEsim.screens.confirm.text" />
        }
        onConfirm={() => startReload?.({ simId: parseInt(simId) })}
        confirmDisabled={!startReload}
        onClose={closeSection}
        startedChange={startedReload}
        changeInProgress={reloadInProgress}
        matchingId={simDetail?.matchingId}
        waitingText={t("changeEsim.waitingText")}
        readyTitle={t("changeEsim.qrCodeTitle")}
        readyDescription={t("changeEsim.qrCodeDone")}
        readyLink={
          <a
            className="text-body-xs font-semibold text-foreground-primary underline"
            href={t2("layout.tiles.qrCodeTile.instructionsLink")}
            target="_blank"
            rel="noreferrer"
          >
            {t("instructionsLinkText")}
          </a>
        }
      />
    );
  }

  if (activeSection === "transferEsim") {
    return (
      <EsimTransferFlow
        dataTestid="transfer-esim"
        cardTitle={t("transferEsim.screens.confirm.title")}
        confirmDescription={
          <Trans t={t} i18nKey="transferEsim.screens.confirm.text" />
        }
        onConfirm={() => startTransfer?.({ simId: parseInt(simId) })}
        confirmDisabled={!startTransfer}
        onClose={closeSection}
        startedChange={startedTransfer}
        changeInProgress={transferInProgress}
        matchingId={simDetail?.matchingId}
        waitingText={t("changeEsim.waitingText")}
        readyTitle={t("changeEsim.qrCodeTitle")}
        readyDescription={t("changeEsim.qrCodeDone")}
        readyLink={
          <a
            className="text-body-xs font-semibold text-foreground-primary underline"
            href={t2("layout.tiles.qrCodeTile.instructionsLink")}
            target="_blank"
            rel="noreferrer"
          >
            {t("instructionsLinkText")}
          </a>
        }
      />
    );
  }

  return (
    <div data-testid="manage-esim" className="flex flex-col gap-4">
      <SimDetailActionRow
        dataTestid="reload-esim-button"
        title={t("reloadEsim.title")}
        text={t("reloadEsim.text")}
        buttonLabel={t("reloadEsim.button")}
        disabled={isBlocked}
        onClick={() => setActiveSection("reloadEsim")}
        disabledAction={
          !canReloadEsim && (
            <EsimDisabledActionsTooltip
              dataTestid="reload-esim-button"
              reasons={esimReloadDisabledReasons}
              disabledButtonText={t("reloadEsim.button")}
            />
          )
        }
      />
      <SimDetailActionRow
        dataTestid="transfer-esim-button"
        title={t("transferEsim.title")}
        text={t("transferEsim.text")}
        buttonLabel={t("transferEsim.button")}
        disabled={isBlocked}
        onClick={() => setActiveSection("transferEsim")}
        disabledAction={
          !canChangeEsim && (
            <EsimDisabledActionsTooltip
              dataTestid="transfer-esim-button"
              reasons={esimTransferDisabledReasons}
              disabledButtonText={t("transferEsim.button")}
            />
          )
        }
      />
    </div>
  );
};
